import { z } from 'zod';
import { avatarConfigSchema } from './avatar.js';
import { childPin } from './auth.js';
import { companionKeySchema } from './companions.js';
import { avatarKindSchema } from './photos.js';
import { readAloudSchema } from './read-aloud.js';
/**
 * Child profile shapes. Nothing about either child is hard-coded anywhere: a
 * profile is created and edited entirely through admin, and these schemas are
 * the only description of what a valid one looks like.
 */
/** F-2 is the curriculum scope; Pre-Prep exists for a child who is not at
 *  school yet and is working towards being ready for it. */
export const YEAR_LEVEL_LABELS = ['Pre-Prep', 'Prep', 'Year 1', 'Year 2'];
export const yearLevelLabelSchema = z.enum(YEAR_LEVEL_LABELS);
export const MISSION_MODES = ['prescribed', 'choice'];
export const missionModeSchema = z.enum(MISSION_MODES);
/**
 * The soft cap is exactly that: the composer sizes a mission to roughly this
 * long and the session ends cleanly there. It never cuts a child off mid-answer
 * and never nags one who wants to keep going.
 */
export const childSettingsSchema = z.object({
    sessionSoftCapMins: z.number().int().min(5).max(45).default(10),
    missionMode: missionModeSchema.default('prescribed'),
    weeklyTargetMissions: z.number().int().min(1).max(14).default(4),
    /** interest tag -> relative weight when the composer themes a mission. */
    themeWeights: z.record(z.number().min(0).max(5)).default({}),
    /**
     * Absent or null means "the default for their year" - see readAloudFor. Null
     * is how a parent who picked one goes back to the default.
     */
    readAloud: readAloudSchema.nullable().optional(),
});
export const DEFAULT_CHILD_SETTINGS = {
    sessionSoftCapMins: 10,
    missionMode: 'prescribed',
    weeklyTargetMissions: 4,
    themeWeights: {},
};
export const interestSchema = z.object({
    tag: z
        .string()
        .trim()
        .min(2)
        .max(32)
        .regex(/^[a-z0-9][a-z0-9 -]*$/i, 'Use letters, numbers, spaces and hyphens only'),
    weight: z.number().min(0.1).max(3).default(1),
    active: z.boolean().default(true),
});
export const setInterestsInput = z.object({
    interests: z.array(interestSchema).max(24),
});
/** A nickname, not a legal name: it is what the child taps on the picker. */
const nickname = z.string().trim().min(1).max(24);
/** Stored and compared as a plain YYYY-MM-DD date. Powers age-at-time
 *  analytics; never shown to a child. */
const dob = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine((d) => {
    const at = new Date(`${d}T00:00:00Z`);
    if (Number.isNaN(at.getTime()))
        return false;
    const years = (Date.now() - at.getTime()) / (365.2425 * 86_400_000);
    return years >= 0 && years < 25;
}, 'That date of birth does not look right');
export const createChildInput = z.object({
    nickname,
    dob,
    yearLevelLabel: yearLevelLabelSchema,
    pin: childPin,
    companionId: companionKeySchema.optional(),
    avatarConfig: avatarConfigSchema.optional(),
    settings: childSettingsSchema.partial().optional(),
    interests: z.array(interestSchema).max(24).optional(),
});
/** Everything a parent may change about a profile. The PIN is deliberately not
 *  here: it has its own audited endpoint. */
export const updateChildInput = z
    .object({
    nickname,
    dob,
    yearLevelLabel: yearLevelLabelSchema,
    companionId: companionKeySchema,
    avatarConfig: avatarConfigSchema,
    settings: childSettingsSchema.partial(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to change');
/** What a child may change about themselves: how they look and who comes with
 *  them. Not their year level, not their targets. */
export const updateOwnProfileInput = z
    .object({
    avatarConfig: avatarConfigSchema,
    companionId: companionKeySchema,
    /** Switching to 'photo' only works if a photo is actually stored; the
     *  server checks, so a child can never end up with a blank face. */
    avatarKind: avatarKindSchema,
    /** A collectible they own, or null to have no buddy. */
    buddyKey: z.string().min(1).max(64).nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to change');
/** The full profile as admin sees it. */
export const adminChildProfile = z.object({
    id: z.string().uuid(),
    nickname: z.string(),
    dob: z.string(),
    ageYears: z.number().int(),
    yearLevelLabel: z.string(),
    companionId: z.string().nullable(),
    avatarConfig: avatarConfigSchema,
    avatarKind: avatarKindSchema,
    hasPhoto: z.boolean(),
    photoUpdatedAt: z.string().nullable(),
    settings: childSettingsSchema,
    /** settings.readAloud resolved against the year level. */
    readAloud: readAloudSchema,
    interests: z.array(interestSchema),
    archived: z.boolean(),
    createdAt: z.string(),
});
/** Whole years old at a given instant, from a YYYY-MM-DD date of birth. */
export function ageYearsAt(dobDay, at = new Date()) {
    const [y, m, d] = dobDay.split('-').map(Number);
    let age = at.getUTCFullYear() - y;
    const beforeBirthday = at.getUTCMonth() + 1 < m || (at.getUTCMonth() + 1 === m && at.getUTCDate() < d);
    if (beforeBirthday)
        age -= 1;
    return age;
}
/**
 * First-run setup, done in the browser instead of over SSH.
 *
 * Guarded twice on the server: it only works while no parent account exists,
 * and it requires the setup code from the server's .env. Once a family exists
 * the endpoint refuses forever, so this can never become a public sign-up form.
 */
export const setupInput = z.object({
    token: z.string().min(1),
    familyName: z.string().trim().min(1).max(60).default('Our Family'),
    parentName: z.string().trim().min(1).max(60),
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(10).max(200),
    children: z
        .array(z.object({
        nickname,
        dob,
        yearLevelLabel: yearLevelLabelSchema,
        pin: childPin,
        interests: z.array(z.string().trim().min(2).max(32)).max(12).default([]),
    }))
        .min(1)
        .max(4),
});
//# sourceMappingURL=profiles.js.map